import { type FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { BsFileEarmarkPlus, BsTrash, BsCaretDownFill, BsFolderPlus } from 'react-icons/bs';
import { Draggable } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import { Link } from 'react-router-dom';

import { type RootState } from '@editor2/reducers/root';
import AddPageModal from '@editor2/components/AddPageModal';
import AddPageGroupModal from '@editor2/components/AddPageGroupModal';
import { deletePageGroupFromChapter } from '@editor2/reducers/editor/editorSlice';
import TableOfContentsPage from './TocPage';
import { pageGroupContainer } from '@editor2/containers/TableOfContentsPage/styles';

interface Props {
	pageGroupId: string;
	chapterId: string;
	index: number;
	courseId: number;
}

const TableOfContentsPageGroup: FC<Props> = (props) => {
	const { pageGroupId, chapterId, index, courseId } = props;

	const dispatch = useDispatch();
	const { page_groups } = useSelector((state: RootState) => state.courses[courseId]);
	const pageGroup = page_groups.entities[pageGroupId];

	const [isAddPageModalShown, setAddPageModalShown] = useState(false);
	const [isAddPageGroupModalShown, setAddPageGroupModalShown] = useState(false);
	const [collapsePages, setCollapsePages] = useState(false);

	const addPageToPageGroup = () => {
		setAddPageModalShown(true);
	};

	const onAddPageGroupClick = () => {
		setAddPageGroupModalShown(true);
	};

	const onDeletePageGroupClick = () => {
		if (window.confirm(`Are you sure to delete "${pageGroup.name}" page group?`)) {
			dispatch(deletePageGroupFromChapter({ chapterId, pageGroupId, courseId }));
		}
	};

	const pageGroupHeader = (
		<div className="pageGroupHeader">
			<div className="pageGroupMenu">
				<BsFolderPlus onClick={() => onAddPageGroupClick()} />
				<BsTrash onClick={() => onDeletePageGroupClick()} />
			</div>
			<Link to={`/courses/${courseId}/chapters/${chapterId}/page_groups/${pageGroup.family_id}`}>
				{pageGroup.name}
			</Link>
			<BsCaretDownFill
				className={collapsePages ? 'collapseIconRotate' : 'collapseIcon'}
				onClick={() => setCollapsePages(!collapsePages)}
			/>
		</div>
	);

	return (
		<Draggable draggableId={pageGroupId} index={index}>
			{(provided) => (
				<div
					className={pageGroupContainer}
					ref={provided.innerRef}
					{...provided.draggableProps}
					{...provided.dragHandleProps}>
					{pageGroupHeader}

					<div className={`pageGroupBody ${collapsePages ? 'listHidden' : 'listShown'}`}>
						<DelayedDroppableWrapper droppableId={pageGroupId} type="Page">
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									{pageGroup.pages.map((pageId, index) => (
										<TableOfContentsPage
											pageId={pageId}
											pageGroupId={pageGroupId}
											chapterId={chapterId}
											courseId={courseId}
											index={index}
											key={pageId}
										/>
									))}
									{provided.placeholder}
								</div>
							)}
						</DelayedDroppableWrapper>

						<Button
							variant="light"
							size="sm"
							className="addPageButton"
							onClick={() => addPageToPageGroup()}>
							<BsFileEarmarkPlus />
							Add page
						</Button>
					</div>

					{isAddPageModalShown && (
						<AddPageModal
							pageGroupId={pageGroupId}
							onHide={() => setAddPageModalShown(false)}
							isShown={isAddPageModalShown}
							courseId={courseId}
						/>
					)}
					{isAddPageGroupModalShown && (
						<AddPageGroupModal
							chapterId={chapterId}
							index={index + 1}
							onHide={() => setAddPageGroupModalShown(false)}
							isShown={isAddPageGroupModalShown}
							courseId={courseId}
						/>
					)}
				</div>
			)}
		</Draggable>
	);
};

export default TableOfContentsPageGroup;
